import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import type { AffiliateUserRole } from "Types/types";
import { CloseOutlined } from "@mui/icons-material";
import { SwipeableDrawer } from "@mui/material";
import { EmailInvitation } from "Components/Affiliate/AffiliateActions/EmailInvitation";
import { QRInvitation } from "Components/Affiliate/AffiliateActions/QRInvitation";

export type DrawerState = "closed" | "qrCode" | "email";

type Props = {
  drawerState: DrawerState;
  onOpen: () => void;
  onClose: () => void;
  affiliateCode: string;
  invitedUserRole: AffiliateUserRole;
};

export const ContentDrawer = ({
  drawerState,
  onOpen,
  onClose,
  affiliateCode,
  invitedUserRole,
}: Props) => {
  const { t } = useResource();

  const drawerStateToContent: Record<DrawerState, ReactNode> = {
    qrCode: <QRInvitation affiliateCode={affiliateCode} />,
    email: (
      <Box mb={[0, 4]}>
        <EmailInvitation invitedUserRole={invitedUserRole} />
      </Box>
    ),
    closed: undefined,
  };

  return (
    // Nice to have: make a component in shared (will be also used in payment detail)
    <SwipeableDrawer
      anchor="bottom"
      open={drawerState !== "closed"}
      onClose={onClose}
      onOpen={onOpen}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          p: 3,
          maxHeight: "85vh",
        },
      }}
      disableDiscovery
      // Modal level, not a hardcoded 10001 — see ResponsiveDialog: a sheet above that
      // level paints over the dropdowns its own content portals to <body>.
      sx={{ zIndex: ({ zIndex }) => zIndex.modal }}
    >
      <Box display="flex" justifyContent="center" mb={6}>
        <Box
          aria-hidden
          sx={{ width: 80, height: 6, bgcolor: "grey.300", borderRadius: 2 }}
        />
        <UnstyledButton
          aria-label={t(Resources.Common.Back)}
          onClick={onClose}
          sx={{
            position: "absolute",
            color: "grey.600",
            right: 12,
            top: 12,
          }}
        >
          <CloseOutlined />
        </UnstyledButton>
      </Box>
      <Box maxWidth={["100%", "500px"]} width="100%" alignSelf="center">
        {drawerStateToContent[drawerState]}
      </Box>
    </SwipeableDrawer>
  );
};
